import { Injectable, inject } from '@angular/core';
import { settingsSelectors, State, UserType } from '@wam/shared';
import { select, Store } from '@ngrx/store';

import { isNil } from 'lodash-es';
import { filter, map, skipWhile, take, tap } from 'rxjs/operators';
import { combineLatest, Observable } from 'rxjs';
import { WebSocketService } from '@app/shared/web-socket.service';
import * as configurationActions from '@app/configuration/state/configuration.actions';
import { isReportsApp } from '@app/reports-app/reports-app.util';

@Injectable({
  providedIn: 'root',
})
export class OrgUpdatesLockingInitializerService {
  private store = inject<Store<State>>(Store);
  private websocketService = inject(WebSocketService);

  initialize(): Observable<boolean> {
    const getSettings$ = this.store.pipe(select(settingsSelectors.getSettings), skipWhile(isNil));
    const userType$ = this.store.pipe(select(settingsSelectors.getUserType));

    return combineLatest([getSettings$, userType$]).pipe(
      // The standalone reports app has no org-locking UI to reflect these websocket events in,
      // and forces `settingsSelectors.getUserType` to `TEACHER` regardless of the actual user (see
      // `settings.reducer.ts`), so this needs an explicit check rather than relying on that alone.
      map(([, userType]) => UserType.TEACHER === userType && !isReportsApp()),
      tap((isTeacher) => {
        if (isTeacher) {
          this.subscribeToLocked();
          this.subscribeToUnlocked();
        }
      }),
      take(1),
    );
  }

  private subscribeToLocked(): void {
    this.websocketService.connect$
      .pipe(filter(({ message }) => message.startsWith('ORG_UPDATES_LOCKED')))
      .subscribe(() => {
        this.store.dispatch(configurationActions.load());
      });
  }

  private subscribeToUnlocked(): void {
    this.websocketService.connect$
      .pipe(filter(({ message }) => message.startsWith('ORG_UPDATES_UNLOCKED')))
      .subscribe(() => {
        this.store.dispatch(configurationActions.load());
      });
  }
}
